import * as React from 'react';
import { Link, useParams, useNavigate } from '@tanstack/react-router';
import { Collection } from '../types';
import { CollectionsService } from '@/services/collections-service';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format, formatDistanceToNow } from 'date-fns';
import { 
  Download, 
  Edit, 
  Eye, 
  FileText, 
  FolderOpen, 
  Info, 
  Trash2
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';

const statusLabels: Record<string, string> = {
  active: 'Active',
  inactive: 'Inactive',
};

const visibilityLabels: Record<string, string> = {
  public: 'Public',
  private: 'Private',
  restricted: 'Restricted',
};

export function CollectionDetailsPage() {
  const { id } = useParams({ from: '/_authenticated/collections/$id' });
  const navigate = useNavigate();
  const [collection, setCollection] = React.useState<Collection | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    fetchCollection();
  }, [id]);

  const fetchCollection = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const fetchedCollection = await CollectionsService.getCollection(id);
      setCollection(fetchedCollection);
    } catch (err) {
      console.error('Error fetching collection:', err);
      setError('Failed to load collection. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!collection) return;
    
    if (window.confirm(`Are you sure you want to delete "${collection.name}"? This action cannot be undone.`)) {
      try {
        setIsDeleting(true);
        await CollectionsService.deleteCollection(collection.id);
        navigate({ to: '/collections' });
      } catch (err) {
        console.error('Error deleting collection:', err);
        toast.error('Failed to delete collection. Please try again.');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading collection...</p>
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <Alert variant="destructive">
        <Info className="h-4 w-4" />
        <AlertTitle>Collection Not Found</AlertTitle>
        <AlertDescription>
          The collection with ID {id} could not be found. It may have been deleted or you may not have permission to view it.
        </AlertDescription>
      </Alert>
    );
  }

  const createdDate = new Date(collection.created_at * 1000);
  const updatedDate = new Date(collection.updated_at * 1000);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <PageHeader
          title={collection.name}
          description={collection.description || 'No description provided'}
        />
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button variant="outline" size="sm">
            <Eye className="mr-2 h-4 w-4" />
            Preview
          </Button>
          <Button asChild size="sm">
            <Link to="/collections/edit/$id" params={{ id: collection.id }}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Link>
          </Button>
          <Button 
            variant="destructive" 
            size="sm" 
            onClick={handleDelete}
            disabled={isDeleting}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge 
          variant="outline" 
          className={
            collection.status === 'active' 
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
          }
        >
          {statusLabels[collection.status]}
        </Badge>
        <Badge 
          variant="outline" 
          className={
            collection.visibility === 'public' 
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
              : collection.visibility === 'private'
                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
          }
        >
          {visibilityLabels[collection.visibility]}
        </Badge>
        <Badge variant="outline">
          <FileText className="mr-1 h-3 w-3" />
          {collection.document_count} Documents
        </Badge>
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Collection Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Created By</span>
                <span className="text-sm text-muted-foreground">User ID: {collection.created_by}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Created At</span>
                <span className="text-sm text-muted-foreground">{format(createdDate, 'PPpp')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Last Updated</span>
                <span className="text-sm text-muted-foreground">{format(updatedDate, 'PPpp')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Status</span>
                <span className="text-sm text-muted-foreground">{statusLabels[collection.status]}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Visibility</span>
                <span className="text-sm text-muted-foreground">{visibilityLabels[collection.visibility]}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Document Count</span>
                <span className="text-sm text-muted-foreground">{collection.document_count}</span>
              </div>
            </CardContent>
          </Card>

          {collection.description && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{collection.description}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="documents" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Documents in Collection</CardTitle>
            </CardHeader>
            <CardContent>
              {collection.documents && collection.documents.length > 0 ? (
                <div className="space-y-4">
                  {collection.documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between border-b pb-3">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center">
                          <FileText className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">{doc.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {doc.document_type} • Created {formatDistanceToNow(new Date(doc.created_at * 1000), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to="/documents/$id" params={{ id: doc.id }}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No documents in this collection.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Collection History</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="mr-4 mt-0.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary">
                      <FolderOpen className="h-4 w-4 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Collection Created</p>
                    <p className="text-sm text-muted-foreground">
                      Created by User ID: {collection.created_by}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {format(createdDate, 'PPpp')}
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="flex items-start">
                  <div className="mr-4 mt-0.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                      <Edit className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Last Updated</p>
                    <p className="text-xs text-muted-foreground">
                      {format(updatedDate, 'PPpp')}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
